import {
	CountryCode as CountryType,
	parsePhoneNumberFromString,
	validatePhoneNumberLength,
} from 'libphonenumber-js';

import countries from 'json/country-codes.json';

export const fetchCountryCodeFromPhone = (
	phoneNumber: string,
	threshold = 7
): string | null => {
	let maxMatch = -1;
	let matchingCountryCode: string | null = null;
	countries.forEach(obj => {
		const countryCode = obj.label.replace('+', '');
		if (phoneNumber.startsWith('+' + countryCode)) {
			const countryCodeLength = obj.label.length;
			const matchPercentage = (countryCodeLength / phoneNumber.length) * 100;
			if (matchPercentage > threshold && countryCodeLength > maxMatch) {
				maxMatch = countryCodeLength;
				matchingCountryCode = obj.label;
			}
		}
	});

	return matchingCountryCode;
};

/**
 * Hard upper bound on how many digits the national number may contain.
 * E.164 allows at most 15 digits including the country calling code, so 15 is
 * a safe ceiling that never rejects a real number. This is only a runaway-input
 * guard — the actual per-country min/max is enforced by `validatePhoneNumber`
 * (libphonenumber-js metadata), NOT by a hardcoded length such as 10/11/12.
 */
export const MAX_PHONE_DIGITS = 15;

/**
 * Resolve the libphonenumber ISO-2 country code (e.g. 'US', 'KY') for a dial-code
 * label (e.g. '+1', '+1345') as stored in country-codes.json. Falls back to 'US'.
 */
export const getIsoCountryCode = (
	dialCodeLabel?: string | number
): CountryType => {
	const countryObj = countries.find(item => item.label === dialCodeLabel);
	return (countryObj?.code as CountryType) ?? 'US';
};

/**
 * Per-country phone-number validation backed by libphonenumber-js metadata.
 *
 * `nationalDigits` is the subscriber number WITHOUT the dial code (digits only).
 * The valid length range (lower AND upper bound) is derived from the selected
 * country's metadata, so countries with 5-digit numbers accept 5 digits and
 * NANP countries accept their full national number — nothing is hardcoded.
 *
 * @returns `{ isValid, message }` — `message` is empty when valid or when the
 * field is empty (so callers don't show an error for an untouched field).
 */
export const validatePhoneNumber = (
	nationalDigits: string,
	dialCodeLabel?: string | number,
	countryName?: string
): { isValid: boolean; message: string } => {
	const digits = (nationalDigits ?? '').replace(/\D/g, '');
	if (!digits) {
		return { isValid: false, message: '' };
	}

	const isoCode = getIsoCountryCode(dialCodeLabel);
	const parsed = parsePhoneNumberFromString(digits, isoCode);
	if (parsed && parsed.isValid()) {
		return { isValid: true, message: '' };
	}

	const forCountry =
		countryName && countryName.length > 0 ? ` for ${countryName}` : '';
	const lengthCheck = validatePhoneNumberLength(digits, isoCode);
	if (lengthCheck === 'TOO_SHORT') {
		return { isValid: false, message: `Mobile number is too short${forCountry}` };
	}
	if (lengthCheck === 'TOO_LONG') {
		return { isValid: false, message: `Mobile number is too long${forCountry}` };
	}
	return { isValid: false, message: `Mobile number is invalid${forCountry}` };
};
